import { type Provider, Service } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { defer, type Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import type { IQuestion } from '@core/models/questionnaire.model';

import type { CompanyProfileMeta } from '../company-profile.model';
import { PublicProfileState } from './public-profile.state';

/** The signed-in company's own public profile. */
@Service({ autoProvided: false })
export class OwnPublicProfileState extends PublicProfileState {
  protected readonly _profileResource = rxResource<CompanyProfileMeta, undefined>({
    stream: () => this._trackServerLoad(this._profileService.getCompanyProfile()),
  });

  /** Discards the draft changes and shows the profile the backend returns. */
  public undo(): Observable<CompanyProfileMeta> {
    return defer(() => {
      this._isUndoing.set(true);

      return this._profileService.undoChanges().pipe(
        tap((profile: CompanyProfileMeta) => this._replaceProfile(profile)),
        finalize(() => this._isUndoing.set(false))
      );
    });
  }

  protected _fetchProfile(): Observable<CompanyProfileMeta> {
    return this._profileService.getCompanyProfile();
  }

  protected _writeField(
    _scope: string,
    fieldName: string,
    value: unknown,
    index?: number
  ): Observable<IQuestion[]> {
    return this._profileService.updateCompanyProfileField(
      fieldName,
      value,
      ...(index !== undefined ? [index] : [])
    );
  }

  protected _addRow(_scope: string, fieldId: string): Observable<unknown> {
    return this._profileService.addListRow(fieldId);
  }

  protected _deleteRow(_scope: string, fieldId: string, index: number): Observable<unknown> {
    return this._profileService.deleteListRow(fieldId, index);
  }

  protected _requestEdit(): Observable<unknown> {
    return this._profileService.editProfileInfo();
  }

  protected _requestValidate(): Observable<unknown> {
    return this._profileService.validateProfileInfo();
  }
}

export function provideOwnPublicProfileState(): Provider[] {
  return [
    OwnPublicProfileState,
    { provide: PublicProfileState, useExisting: OwnPublicProfileState },
  ];
}
